/**
 * 벌점 부과 모달
 */
import React, { useState, useEffect } from 'react';
import { PenaltyType, PenaltyRecordCreate } from '../../models/Penalty';
import { memberRepository } from '../../repositories';
import { getTodayKST } from '../../../../utils/dateUtils';

interface Student {
  id: string;
  name: string;
  memberNumber?: string;
}

interface PenaltyIssueModalProps {
  types: PenaltyType[];
  onClose: () => void;
  onSubmit: (data: Omit<PenaltyRecordCreate, 'organization_id'>) => Promise<void>;
  onComplete: () => void;
}

export const PenaltyIssueModal: React.FC<PenaltyIssueModalProps> = ({
  types,
  onClose,
  onSubmit,
  onComplete,
}) => {
  const [formData, setFormData] = useState({
    student_id: '',
    penalty_type_code: '',
    points: 1,
    point_type: 'demerit' as 'merit' | 'demerit',
    reason: '',
    occurred_date: getTodayKST(),
    occurred_time: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 학생 목록
  const [students, setStudents] = useState<Student[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // 학생 목록 로드
  useEffect(() => {
    const loadStudents = async () => {
      try {
        setLoadingStudents(true);
        const response = await memberRepository.getMembers({ limit: 500 });
        const studentList = (response.members || []).map((m) => ({
          id: m.id,
          name: m.name,
          memberNumber: m.memberNumber,
        }));
        setStudents(studentList);
      } catch (e) {
        console.error('학생 목록 로드 실패:', e);
      } finally {
        setLoadingStudents(false);
      }
    };
    loadStudents();
  }, []);

  // 검색 필터링된 학생 목록
  const filteredStudents = students.filter(
    (s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.memberNumber && s.memberNumber.includes(searchTerm))
  );

  // 유형 선택 시 기본 점수 설정
  useEffect(() => {
    if (formData.penalty_type_code) {
      const selectedType = types.find((t) => t.code === formData.penalty_type_code);
      if (selectedType) {
        setFormData((prev) => ({ ...prev, points: selectedType.default_points }));
      }
    }
  }, [formData.penalty_type_code, types]);

  // 활성화된 유형만 필터 (상점/벌점에 따라 분리)
  const activeTypes = types.filter((t) => t.is_active && (t.point_type || 'demerit') === formData.point_type);

  // 폼 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.student_id) {
      setError('학생을 선택해주세요.');
      return;
    }
    if (!formData.penalty_type_code) {
      setError('유형을 선택해주세요.');
      return;
    }
    if (!formData.reason.trim()) {
      setError('사유를 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit({
        student_id: formData.student_id,
        penalty_type_code: formData.penalty_type_code,
        points: formData.points,
        point_type: formData.point_type,
        reason: formData.reason,
        occurred_date: formData.occurred_date,
        occurred_time: formData.occurred_time || undefined,
      });
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : '벌점 부과에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            {formData.point_type === 'merit' ? '상점 부과' : '벌점 부과'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* 에러 메시지 */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* 상점/벌점 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">구분 *</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, point_type: 'merit', penalty_type_code: '', points: 1 })}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${
                  formData.point_type === 'merit'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                ⬆ 상점
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, point_type: 'demerit', penalty_type_code: '', points: 1 })}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${
                  formData.point_type === 'demerit'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                ⬇ 벌점
              </button>
            </div>
          </div>

          {/* 학생 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">학생 선택 *</label>
            {loadingStudents ? (
              <div className="w-full border rounded-lg px-3 py-2 bg-gray-50 text-gray-500">
                학생 목록 로딩 중...
              </div>
            ) : students.length > 0 ? (
              <div className="space-y-2">
                {/* 검색 입력 */}
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="이름 또는 회원번호로 검색..."
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                {/* 학생 드롭다운 */}
                <select
                  value={formData.student_id}
                  onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                  size={5}
                >
                  <option value="">학생을 선택하세요 ({filteredStudents.length}명)</option>
                  {filteredStudents.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.name} {student.memberNumber ? `(${student.memberNumber})` : ''}
                    </option>
                  ))}
                </select>
                {formData.student_id && (
                  <p className="text-xs text-green-600">
                    선택: {students.find((s) => s.id === formData.student_id)?.name}
                  </p>
                )}
              </div>
            ) : (
              <div className="text-sm text-red-500">
                학생 목록을 불러올 수 없습니다. 새로고침 후 다시 시도해주세요.
              </div>
            )}
          </div>

          {/* 유형 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {formData.point_type === 'merit' ? '상점' : '벌점'} 유형 *
            </label>
            <select
              value={formData.penalty_type_code}
              onChange={(e) => setFormData({ ...formData, penalty_type_code: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              required
            >
              <option value="">유형을 선택하세요</option>
              {activeTypes.map((type) => (
                <option key={type.id} value={type.code}>
                  {type.name} (기본 {type.default_points}점)
                </option>
              ))}
            </select>
          </div>

          {/* 점수 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">점수 *</label>
            <input
              type="number"
              value={formData.points}
              onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 1 })}
              min={1}
              max={100}
              className="w-full border rounded-lg px-3 py-2"
              required
            />
            <p className="text-xs text-gray-500 mt-1">유형 선택 시 기본 점수가 자동으로 설정됩니다.</p>
          </div>

          {/* 발생 날짜 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">발생 날짜 *</label>
            <input
              type="date"
              value={formData.occurred_date}
              onChange={(e) => setFormData({ ...formData, occurred_date: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
              required
            />
            {/* 2026-10-02: 점수는 '벌점이 생긴 날'의 달로 들어간다 — 지난달 날짜면 이번 달 현황엔 안 보인다고 미리 알린다 */}
            {formData.occurred_date && formData.occurred_date < `${getTodayKST().slice(0, 8)}01` && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-1">
                지난달 날짜입니다. 이 점수는 지난달 점수로 들어가며, 이번 달 상벌점 현황에는 보이지 않습니다.
                기록은 「상벌점 기록」 탭에 남습니다.
              </p>
            )}
          </div>

          {/* 발생 시간 (선택) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">발생 시간</label>
            <input
              type="time"
              value={formData.occurred_time}
              onChange={(e) => setFormData({ ...formData, occurred_time: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          {/* 사유 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">사유 *</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="사유를 입력하세요..."
              className="w-full border rounded-lg px-3 py-2"
              rows={3}
              required
            />
          </div>

          {/* 버튼 */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              disabled={isSubmitting}
            >
              취소
            </button>
            <button
              type="submit"
              className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                formData.point_type === 'merit'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? '처리 중...' : formData.point_type === 'merit' ? '상점 부과' : '벌점 부과'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
